import { AppDispatch, AppStorageType } from '@app-types/appStorageType';
import { apiCall } from '@commands/httpClient';
import { ResultItemRes, ResultListRes } from '@modules/apiClient/type';
import { User } from '@modules/auth/types/Users';
import { Status } from '@modules/status/types';
import { Client, IBaseUser, IStudent, ITeacher, Online, Student, Teacher } from '@modules/user/types';
import {
  appendUserAction, setStudentsAction,
  setUserAction,
  setUsersAction,
  teachersCountAction,
  usersCountAction,
  clearTeachersAction,
} from '@modules/user/actions';
import { Document } from '@modules/auth/types';
import { BaseCommandProps } from '@app-types/CommonTypes';
import get from 'lodash/get';
import { InitiatedStatistic } from '@modules/schedule';
import { selectStudentByAdmin } from '@commands/schedule/admin';
import { StageLesson } from '@modules/investments/products/types/stage';
import { Purchase } from '@modules/investments/purchases/types/purchase';

export const loadTeachers = (subjectId: string, offset?: number, limit?: number) => (dispatch: AppDispatch<ResultListRes<ITeacher[]>>, getState: () => AppStorageType) => {
  const state: AppStorageType = getState();
  const country = state.country.country;
  return dispatch(apiCall<object, ResultListRes<ITeacher[]> & { count: number }>(`/v1/teacher/list`, {
    offset,
    subjectId,
    country,
    limit,
  }, { method: 'get' }))
    .then(({ list, count }) => {
      const items = list.map(i => {
        return new Teacher({
          user: new User(i.user),
          status: new Status(i.status),
          document: i.document && new Document(i.document),
          online: i.online && new Online(i.online),
        }, i.classesHeld);
      });
      dispatch(setUsersAction(items));
      dispatch(teachersCountAction(count));
    });
};

export const clearTeachers = () => (dispatch: AppDispatch<ResultListRes<ITeacher[]>>) => {
  return dispatch(clearTeachersAction());
};

export const loadStudents = () => (dispatch: AppDispatch<ResultListRes<ITeacher[]>>) => {
  return dispatch(apiCall<object, ResultListRes<ITeacher[]>>(`/v1/students/list`, undefined, { method: 'get' }))
    .then(({ list }) => {
      const items = (list || []).map(i => {
        return new Student({
          user: new User(i.user),
          status: new Status(i.status),
          document: i.document && new Document(i.document),
          online: i.online && new Online(i.online),
          statistic: i.statistic && new InitiatedStatistic(i.statistic),
        });
      });
      dispatch(setStudentsAction(items));
    });
};

export const adminLoadStudents = () => (dispatch: AppDispatch<ResultListRes<ITeacher[]>>, getState: () => AppStorageType) => {
  const state: AppStorageType = getState();
  const userId = state.adminSchedule.teacher;

  if (!userId) {
    return;
  }

  return dispatch(apiCall<object, ResultListRes<ITeacher[]>>(`/v1/admin/students/list`, { userId }, { method: 'post' }))
    .then(({ list }) => {
      const items = (list || []).map(i => {
        return new Student({
          user: new User(i.user),
          status: new Status(i.status),
          document: i.document && new Document(i.document),
          online: i.online && new Online(i.online),
          statistic: i.statistic && new InitiatedStatistic(i.statistic),
        });
      });
      dispatch(setStudentsAction(items));
      dispatch(selectStudentByAdmin(get(items, '0.user.id')));
    });
};

export const loadUser = (id: number) => (dispatch: AppDispatch<ResultItemRes<IBaseUser>>, getState: () => AppStorageType) => {
  const state: AppStorageType = getState();

  const user = state.users.list.find(i => i.user.id === id);
  if (user) {
    return dispatch(setUserAction(user));
  }

  return dispatch(apiCall<object, ResultItemRes<ITeacher | IStudent>>(`/v1/user/get/${id}`, undefined, { method: 'get' }))
    .then(({ item }) => {
      const role = get(item, 'user.role');
      let value;
      if (role === 'student') {
        const purchase = item.purchase && new Purchase<StageLesson>(item.purchase, item.purchase?.stage || {});
        value = new Student({
          user: new User(item.user),
          status: new Status(item.status),
          document: new Document(item.document),
          online: item.online && new Online(item.online),
          statistic: item.statistic && new InitiatedStatistic(item.statistic),
        }, purchase);
      } else {
        value = new Teacher({
          user: new User(item.user),
          status: new Status(item.status),
          document: new Document(item.document),
          online: item.online && new Online(item.online),
        }, item.classesHeld);
      }

      dispatch(appendUserAction(value));
      dispatch(setUserAction(value));
    });
};

export const loadUserById = (id: number) => (dispatch: AppDispatch<ResultItemRes<IBaseUser>>, getState: () => AppStorageType) => {
  const state: AppStorageType = getState();

  const user = state.users.list.find(i => i.user.id === id);
  if (user) {
    return user;
  }

  return dispatch(apiCall<object, ResultItemRes<ITeacher | IStudent>>(`/v1/user/get/${id}`, undefined, { method: 'get' }))
    .then(({ item }) => new Teacher({
        user: new User(item.user),
        status: new Status(item.status),
        document: new Document(item.document),
        online: item.online && new Online(item.online),
      }, 0),
    );
};

export const updateLang = (userId: number, langId: number) => (dispatch: AppDispatch<any>) => {
  return dispatch(apiCall<object, any>(`/v1/students/update-lang`, {
    userId,
    langId,
  }, { method: 'put' }));
};

export const searchUser = (query: string, role: 'student' | 'teacher', deleted: boolean = false) => (dispatch: AppDispatch<Client[]>) => {
  return dispatch(apiCall<object, ResultListRes<Client[]>>(`/v1/admin/user/search`, {
    query,
    role,
    deleted,
  }, { method: 'post' }))
    .then(({ list }) => {
      const items = list.map((i) =>
        new Client({
          user: new User(i.user),
          status: new Status(i.status),
          document: i.document && new Document(i.document),
          online: i.online && new Online(i.online),
        }),
      );
      dispatch(setUsersAction(items));
      dispatch(usersCountAction(10));
      return items;
    });
};

export const loadAdminTeachers = (country: string, subjectId: number, offset?: number) => (dispatch: AppDispatch<Teacher[]>) =>
  dispatch(apiCall<object, ResultListRes<ITeacher[]>>(`/v1/teacher/admin/list`, { subjectId, offset, country }, { method: 'get' }))
    .then(({ list }) => list.map(i =>
        new Teacher({
          user: new User(i.user),
          status: new Status(i.status),
          document: i.document && new Document(i.document),
          online: i.online && new Online(i.online),
        }, i.classesHeld),
      ),
    );

export const loadAdminUsers = (currentPage: number, limit: number, role: string, deleted: boolean = false) => (dispatch: AppDispatch<Client[]>) => {
  const offset = (currentPage - 1) * limit;
  return dispatch(apiCall<object, ResultListRes<Client[]>>(`/v1/users/${offset}/${limit}/${role}`, { deleted }, { method: 'get' }))
    .then(({ list }) => {
      const items = list.map(i => {
        return new Client({
          user: new User(i.user),
          status: new Status(i.status),
          document: i.document && new Document(i.document),
          online: i.online && new Online(i.online),
        });
      });
      dispatch(setUsersAction(items));
    });
};

export const loadAdminUsersCount = (role: string, deleted: boolean = false) => (dispatch: AppDispatch<number>) => {
  return dispatch(apiCall<object, {
    count: number
  } & BaseCommandProps>(`/v1/users/count/${role}`, { deleted }, { method: 'get' }))
    .then(({ count }) => dispatch(usersCountAction(count)));
};
